import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";

const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const CRM_URL = "https://chat.berrybyteinn.com/";

const MODULES = [
  {
    badge: "Close deals faster",
    title: "CRM & Pipeline",
    desc: "Manage every contact, lead, company, and deal from one workspace. Visual Kanban pipeline with real-time revenue forecasting and full activity timeline.",
    features: ["Contact & lead management", "Visual Kanban pipeline", "Deal tracking & forecasting", "Company linking & history"],
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
    badgeColor: "border-violet-500/30 bg-violet-500/10 text-violet-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008z" />
      </svg>
    ),
  },
  {
    badge: "Never miss a message",
    title: "AI-Powered Chat",
    desc: "GPT-4o chatbot handles routine questions 24/7 and suggests perfect replies for your agents instantly. Learns from your knowledge base automatically.",
    features: ["24/7 AI auto-replies", "Agent response suggestions", "Intent detection & routing", "Seamless human handover"],
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "text-amber-400",
    badgeColor: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456z" />
      </svg>
    ),
  },
  {
    badge: "Talk to every visitor",
    title: "Live Chat Widget",
    desc: "Embed real-time chat on any website in 2 minutes with a single line of code. WebSocket-powered with typing indicators and visitor identity capture.",
    features: ["1-line embed snippet", "Real-time WebSocket chat", "Visitor identity capture", "Cross-domain support"],
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
    badgeColor: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
  },
  {
    badge: "Zero requests slip through",
    title: "Ticket System",
    desc: "Full helpdesk with SLA rules, priority queues, AI ticket summaries, and smart routing. Every chat converts to a ticket with one click.",
    features: ["SLA rules & escalation", "Priority & status workflows", "AI ticket summarisation", "Agent assignment & routing"],
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "text-emerald-400",
    badgeColor: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M16.5 6v.75a3.75 3.75 0 0 1-7.5 0V6m-4.125 9h15.75A2.625 2.625 0 0 0 23.25 12.375V9.75A2.625 2.625 0 0 0 20.625 7.125H3.375A2.625 2.625 0 0 0 .75 9.75v2.625A2.625 2.625 0 0 0 3.375 15z" />
      </svg>
    ),
  },
  {
    badge: "Insights that drive action",
    title: "Analytics & Reports",
    desc: "Real-time dashboards for revenue, team performance, and customer satisfaction. Filter by date range, agent, or channel.",
    features: ["Revenue & pipeline charts", "Team performance metrics", "CSAT & response time", "Conversion funnels"],
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
    badgeColor: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    badge: "Let the platform do the work",
    title: "Workflow Automations",
    desc: "No-code automation builder triggered by any CRM event. Auto-assign leads, send follow-ups, update deals, fire webhooks automatically.",
    features: ["12+ trigger types", "Conditions & filters", "Multi-step action chains", "Scheduling & delays"],
    color: "border-sky-500/30 bg-sky-500/5",
    accent: "text-sky-400",
    badgeColor: "border-sky-500/30 bg-sky-500/10 text-sky-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
      </svg>
    ),
  },
  {
    badge: "Self-service that works",
    title: "Knowledge Base",
    desc: "Publish a branded public help center in minutes. Markdown editor, categories, full-text search, and automatic AI training from your articles.",
    features: ["Public help center URL", "Markdown article editor", "Full-text search", "AI chatbot training"],
    color: "border-fuchsia-500/30 bg-fuchsia-500/5",
    accent: "text-fuchsia-400",
    badgeColor: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  {
    badge: "Compliance without compromise",
    title: "Enterprise Security",
    desc: "Multi-tenant architecture with strict data isolation, JWT auth, 5-level RBAC, complete audit logging, and optional SSO/SAML.",
    features: ["5-level RBAC system", "Full audit logs", "JWT + refresh tokens", "SSO / SAML (Enterprise)"],
    color: "border-red-500/30 bg-red-500/5",
    accent: "text-red-400",
    badgeColor: "border-red-500/30 bg-red-500/10 text-red-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
      </svg>
    ),
  },
  {
    badge: "Scale your support team",
    title: "Team Management",
    desc: "Invite agents, create teams, set roles, and route conversations intelligently with full visibility into workload and performance.",
    features: ["Agent invitations", "Smart conversation routing", "Team performance dashboard", "Agent availability status"],
    color: "border-indigo-500/30 bg-indigo-500/5",
    accent: "text-indigo-400",
    badgeColor: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z" />
      </svg>
    ),
  },
  {
    badge: "Connect everything you use",
    title: "Integrations & API",
    desc: "Full REST API, event-driven webhooks, and native integrations with Stripe, Slack, Zapier, and more. Build custom integrations with our documented API.",
    features: ["Full REST API", "Event-driven webhooks", "Stripe billing built-in", "Zapier & Slack integrations"],
    color: "border-orange-500/30 bg-orange-500/5",
    accent: "text-orange-400",
    badgeColor: "border-orange-500/30 bg-orange-500/10 text-orange-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
    ),
  },
];

const STATS = [
  { value: "10", label: "Powerful Modules" },
  { value: "200+", label: "Businesses Using It" },
  { value: "24/7", label: "AI Always On" },
  { value: "2 min", label: "Widget Setup" },
];

export default function CRMPage() {
  useSEO({
    title: "BerryChat | CRM, Live Chat & Support | BerryByte",
    description: "BerryChat — all-in-one CRM platform: AI chat, live widget, ticketing, pipeline management, automations, analytics & knowledge base for teams.",
    keywords: "CRM platform for small business, customer support software, live chat CRM, AI powered CRM, helpdesk software US Canada, BerryChat CRM, sales pipeline management, workflow automation CRM",
    canonical: "https://www.berrybyteinn.com/crm",
  });

  return (
    <SharedLayout>
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[800px] h-[800px] bg-violet-600 -top-40 -left-40" />
        <GlowOrb className="w-[600px] h-[600px] bg-cyan-600 top-1/3 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
              Built by BerryByte · Used by 200+ growing businesses
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Everything you need
              <br />
              <span className="bg-gradient-to-r from-violet-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                to grow.
              </span>
            </h1>

            <p className="text-xl text-slate-400 leading-relaxed mb-4 max-w-3xl">
              10 powerful modules. One integrated platform. Built for modern teams at every stage.
            </p>
            <p className="text-lg text-slate-500 leading-relaxed mb-10 max-w-2xl">
              BerryChat brings together CRM, AI-powered chat, live widget, ticketing, workflow automation, analytics, knowledge base, and enterprise security — all from a single workspace.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={CRM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold text-base shadow-2xl shadow-violet-900/40 hover:shadow-violet-900/60 hover:-translate-y-1 transition-all duration-300"
              >
                Get Started Free
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 group-hover:translate-x-1 transition-transform">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href={CRM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold text-base bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300"
              >
                See Live Demo
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-5 text-sm text-slate-400">
              {["No credit card required", "Set up in 2 minutes", "Cancel anytime"].map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-emerald-400">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative py-12 bg-[#070b14] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl font-black text-white mb-1">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10 Modules */}
      <section className="relative py-24 bg-[#050810]">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-violet-400 font-semibold text-sm tracking-widest uppercase">Platform Modules</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">
              10 powerful modules.
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                One integrated platform.
              </span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              Built for modern teams at every stage — from first customer to enterprise scale.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map((mod) => {
              const Icon = mod.icon;
              return (
                <div
                  key={mod.title}
                  className={`group rounded-2xl border ${mod.color} p-6 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${mod.accent}`}>
                      <Icon />
                    </div>
                    <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-medium ${mod.badgeColor}`}>
                      {mod.badge}
                    </span>
                  </div>

                  <h3 className={`text-xl font-black text-white mb-2`}>{mod.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5">{mod.desc}</p>

                  <div className="space-y-2">
                    {mod.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <span className={`flex-shrink-0 w-4 h-4 rounded-full bg-white/5 border border-white/10 flex items-center justify-center ${mod.accent}`}>
                          <Check />
                        </span>
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual dashboard mockup */}
      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] bg-violet-700 top-1/2 -translate-y-1/2 -left-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-cyan-400 font-semibold text-sm tracking-widest uppercase">One Workspace</span>
              <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white leading-tight">
                Everything in one place,
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  nothing falls through.
                </span>
              </h2>
              <p className="mt-6 text-slate-400 text-lg leading-relaxed">
                No more switching between five different tools. BerryChat brings your CRM, chat, support, and analytics into a single workspace — so your team can move faster and your customers get better experiences.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  "Close more deals with a visual Kanban pipeline and real-time forecasting",
                  "Let AI handle routine questions while your agents focus on high-value conversations",
                  "Automate lead assignment, follow-ups, and deal updates with no-code workflows",
                  "Get full visibility with dashboards that track revenue, CSAT, and team performance",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mt-0.5">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-slate-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <a
                href={CRM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold hover:from-violet-500 hover:to-cyan-500 hover:-translate-y-1 transition-all duration-300 shadow-2xl shadow-violet-900/40"
              >
                Get Started Free
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>

            {/* Dashboard Preview */}
            <div className="relative">
              <div className="rounded-3xl border border-white/10 bg-[#0a0d18] overflow-hidden shadow-2xl shadow-black/60">
                {/* Browser bar */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#080b14]">
                  <div className="w-3 h-3 rounded-full bg-rose-500/50" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/50" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/50" />
                  <div className="flex-1 mx-4 h-5 rounded bg-white/5 flex items-center px-3">
                    <span className="text-slate-600 text-xs">chat.berrybyteinn.com</span>
                  </div>
                </div>

                <div className="flex h-80">
                  {/* Sidebar */}
                  <div className="w-40 border-r border-white/5 p-3 flex flex-col gap-1 flex-shrink-0">
                    {[
                      { label: "Dashboard", active: true },
                      { label: "CRM & Pipeline", active: false },
                      { label: "Live Chat", active: false },
                      { label: "Tickets", active: false },
                      { label: "Automations", active: false },
                      { label: "Analytics", active: false },
                      { label: "Knowledge Base", active: false },
                      { label: "Team", active: false },
                      { label: "Integrations", active: false },
                    ].map(({ label, active }) => (
                      <div
                        key={label}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium ${active ? "bg-violet-600 text-white" : "text-slate-500"}`}
                      >
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Main content */}
                  <div className="flex-1 p-4 overflow-hidden">
                    {/* Top stats */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[
                        { label: "Pipeline Value", value: "$124K", color: "text-violet-400" },
                        { label: "Open Tickets", value: "24", color: "text-amber-400" },
                        { label: "CSAT Score", value: "4.9", color: "text-emerald-400" },
                      ].map((s) => (
                        <div key={s.label} className="p-2 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                          <div className={`font-bold text-base ${s.color}`}>{s.value}</div>
                          <div className="text-slate-600 text-xs">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Kanban columns preview */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { stage: "Leads", count: 8, color: "bg-slate-500/20" },
                        { stage: "Qualified", count: 5, color: "bg-violet-500/20" },
                        { stage: "Closed", count: 3, color: "bg-emerald-500/20" },
                      ].map((col) => (
                        <div key={col.stage} className="rounded-lg p-2 bg-white/[0.02] border border-white/5">
                          <div className="text-slate-500 text-xs mb-1">{col.stage}</div>
                          <div className={`h-1.5 rounded-full ${col.color} mb-1`} />
                          <div className="text-white text-xs font-semibold">{col.count} deals</div>
                        </div>
                      ))}
                    </div>

                    {/* Recent chats */}
                    <div className="space-y-1.5">
                      {[
                        { name: "Sarah M.", msg: "How do I upgrade?", status: "AI replied", dot: "bg-emerald-400" },
                        { name: "John D.", msg: "Reset password help", status: "Open", dot: "bg-amber-400" },
                        { name: "Lisa K.", msg: "Billing question", status: "Resolved", dot: "bg-slate-500" },
                      ].map((chat) => (
                        <div key={chat.name} className="flex items-center gap-2 p-1.5 rounded-lg bg-white/[0.02] border border-white/5">
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${chat.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-white text-xs font-semibold">{chat.name}</span>
                              <span className="text-slate-600 text-xs">{chat.status}</span>
                            </div>
                            <div className="text-slate-500 text-xs truncate">{chat.msg}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating AI suggestion bubble */}
              <div className="absolute -bottom-4 -right-4 w-52 rounded-2xl border border-amber-500/20 bg-[#0a0d18] shadow-2xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" className="w-3 h-3">
                      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09z" />
                    </svg>
                  </div>
                  <span className="text-amber-400 text-xs font-semibold">AI Suggestion</span>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">"Hi John! You can reset your password from Settings → Security → Change Password."</p>
                <div className="mt-2 flex gap-1.5">
                  <div className="flex-1 text-center py-1 rounded bg-amber-500/20 text-amber-300 text-xs font-medium cursor-pointer">Use</div>
                  <div className="flex-1 text-center py-1 rounded bg-white/5 text-slate-400 text-xs cursor-pointer">Edit</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Free to get started · No credit card required
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
            Ready to transform
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              how your team works?
            </span>
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Join hundreds of businesses using BerryChat's 10 integrated modules to close more deals, support more customers, and grow faster.
          </p>
          <a
            href={CRM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold text-lg shadow-2xl shadow-violet-900/40 hover:shadow-violet-900/60 hover:-translate-y-1 transition-all duration-300"
          >
            Get Started Free
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
          <p className="mt-4 text-slate-600 text-sm">
            Questions?{" "}
            <a href="tel:+12018469830" className="text-slate-400 hover:text-white transition-colors">
              Call +1 201-846-9830
            </a>
          </p>
        </div>
      </section>
    </SharedLayout>
  );
}
